import { useCallback, useLayoutEffect, useState } from 'react';
import './textareaScrollbars.css';

const EDGE_EPSILON = 1;
const FADE_HEIGHT = 20;

/** Fade only the text viewport, leaving the field outline and toolbar untouched. */
export default function useTextareaScrollFade(value?: unknown) {
  const [textarea, setTextarea] = useState<HTMLTextAreaElement | null>(null);
  const ref = useCallback((node: HTMLInputElement | HTMLTextAreaElement | null) => {
    setTextarea(node?.tagName === 'TEXTAREA' ? node as HTMLTextAreaElement : null);
  }, []);

  const update = useCallback(() => {
    if (!textarea) return;
    const maxScroll = textarea.scrollHeight - textarea.clientHeight;
    const top = maxScroll > EDGE_EPSILON && textarea.scrollTop > EDGE_EPSILON;
    const bottom = maxScroll > EDGE_EPSILON && textarea.scrollTop < maxScroll - EDGE_EPSILON;
    const fade = Math.min(FADE_HEIGHT, textarea.clientHeight / 4);
    const mask = top || bottom
      ? `linear-gradient(to bottom, ${top ? `transparent, #000 ${fade}px` : '#000 0%'}, ${bottom ? `#000 calc(100% - ${fade}px), transparent` : '#000 100%'})`
      : 'none';
    textarea.style.maskImage = mask;
    textarea.style.webkitMaskImage = mask;
  }, [textarea]);

  useLayoutEffect(() => {
    if (!textarea) return;
    textarea.setAttribute('data-fable-scroll-fade', '');
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    observer?.observe(textarea);
    textarea.addEventListener('scroll', update);
    textarea.addEventListener('input', schedule);
    window.addEventListener('resize', schedule);
    return () => {
      textarea.removeAttribute('data-fable-scroll-fade');
      cancelAnimationFrame(frame);
      observer?.disconnect();
      textarea.removeEventListener('scroll', update);
      textarea.removeEventListener('input', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [textarea, update]);

  // Controlled values can change without an input event (clear, restore, suggestions).
  useLayoutEffect(() => {
    update();
    const frame = requestAnimationFrame(update);
    return () => cancelAnimationFrame(frame);
  }, [update, value]);

  return ref;
}
